/**
 * Avatar — affiche soit la photo, soit les initiales avec un dégradé doré.
 */
import { colors, fonts } from "../styles/tokens";

const SIZES = {
  xs: 24,
  sm: 32,
  md: 40,
  lg: 56,
  xl: 80,
  "2xl": 120,
};

/** Calcule les initiales depuis un nom complet (max 2 caractères). */
function getInitials(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0][0]?.toUpperCase() || "?";
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Génère une couleur stable basée sur la chaîne (hash simple). */
function colorFromName(name = "") {
  const palette = [
    "linear-gradient(135deg, #E4A72C 0%, #F5C55A 100%)",   // or signature
    "linear-gradient(135deg, #B8861F 0%, #E4A72C 100%)",   // or sombre
    "linear-gradient(135deg, #1E2A3E 0%, #0F1520 100%)",   // bleu nuit
    "linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%)",   // bleu info
    "linear-gradient(135deg, #2DD4A0 0%, #1B9D78 100%)",   // vert succès
  ];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return palette[h % palette.length];
}

export default function Avatar({
  name = "",
  src = null,
  size = "md",
  online = false,
  bordered = false,
  onClick = null,
}) {
  const px = SIZES[size] || SIZES.md;
  const fontSize = px * 0.4;

  const wrapperStyle = {
    width: px,
    height: px,
    borderRadius: "50%",
    background: src ? colors.surface : colorFromName(name),
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: colors.textBright,
    fontFamily: fonts.body,
    fontSize: `${fontSize}px`,
    fontWeight: 600,
    flexShrink: 0,
    overflow: "hidden",
    position: "relative",
    border: bordered ? `2px solid ${colors.gold}` : "none",
    cursor: onClick ? "pointer" : "default",
    boxShadow: bordered ? "0 0 0 3px rgba(228,167,44,0.18)" : "none",
    transition: "transform 200ms",
  };

  const dotStyle = {
    position: "absolute",
    bottom: 0,
    right: 0,
    width: Math.max(8, px * 0.22),
    height: Math.max(8, px * 0.22),
    borderRadius: "50%",
    background: colors.success,
    border: `2px solid ${colors.bg}`,
  };

  return (
    <div style={wrapperStyle} onClick={onClick} title={name}>
      {src ? (
        <img
          src={src}
          alt={name}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      ) : (
        <span>{getInitials(name)}</span>
      )}
      {online && <span style={dotStyle} />}
    </div>
  );
}
